import * as Sentry from '@sentry/vue'
import type { App } from 'vue'
import type { UserInfo } from '@/types/api'
import { logger } from '@/utils/logger'

type SentryEntry = 'main' | 'sidebar' | 'floating-ball'

export function shouldInitSentry(dsn: string | undefined): boolean {
  return parseSentryDsn(dsn) !== null
}

export function parseSentryDsn(dsn: string | undefined): URL | null {
  const trimmed = dsn?.trim()
  if (!trimmed) return null

  try {
    const url = new URL(trimmed)
    const hasValidProtocol = url.protocol === 'https:' || url.protocol === 'http:'
    const hasPublicKey = Boolean(url.username)
    const hasProjectId = Boolean(url.pathname.replace(/^\/+|\/+$/g, ''))
    return hasValidProtocol && Boolean(url.hostname) && hasPublicKey && hasProjectId ? url : null
  } catch {
    return null
  }
}

export function getSentryRelease(version: string | undefined): string {
  return `aihelper@${version?.trim() || __APP_VERSION__}`
}

export function getSentryTracesSampleRate(environment: string): number {
  return environment === 'production' ? 0.1 : 1.0
}

export function getSentryAllowUrls(environment = 'development'): RegExp[] {
  const allowUrls = [/tauri:\/\//, /https?:\/\/tauri\.localhost/, /https?:\/\/asset\.localhost/]
  if (environment !== 'production') {
    allowUrls.push(/^http:\/\/localhost/)
  }
  return allowUrls
}

export function getSentryIgnoreErrors(): (string | RegExp)[] {
  return [
    'ResizeObserver loop limit exceeded',
    'Non-Error promise rejection captured',
    'TokenExpiredError',
    '登录已过期，请重新登录',
    /jwt expired/i,
    /token expired/i,
    /Failed to fetch/,
    /Window has been closed/,
    /IPC channel closed/,
  ]
}

const SENTRY_VALIDATION_NOISE_PATTERNS = [
  'Codex Sentry DingTalk',
  'Codex DingTalk alert retest',
  '[DEMO] Test error upload',
  'sentry test error',
  'smoke test from AIHelper sentry-0 setup',
  'aihelper-web smoke test 1.0.6',
  'windows staging sentry web runtime validation',
  'windows production sentry web runtime validation',
  'windows staging sentry web envelope validation',
  'windows production sentry web envelope validation',
  'windows production installed web validation',
  'windows production installed envelope verification',
  'web_sentry_fixed_win_',
]

const TOKEN_EXPIRED_PATTERNS = ['TokenExpiredError', '登录已过期，请重新登录']

const OCR_DOWNLOAD_NOISE_PATTERNS = [
  '[OCR] Download ',
  '[OCR] Failed to read response',
  '[OCR] Failed to write ',
  '[OCR] Failed to create tessdata dir',
]
const MAX_REASON_KEYS = 8

function safeString(value: unknown): string {
  if (value === null || value === undefined) return ''
  try {
    return String(value)
  } catch {
    return ''
  }
}

function getProperty(value: unknown, key: string): unknown {
  if ((typeof value !== 'object' && typeof value !== 'function') || value === null) return undefined
  try {
    return key in value ? (value as Record<string, unknown>)[key] : undefined
  } catch {
    return undefined
  }
}

function getMessageText(value: unknown): string {
  if (typeof value === 'string') return value
  if (value instanceof Error) return safeString(getProperty(value, 'message'))
  const message = getProperty(value, 'message')
  if (message !== undefined) {
    return safeString(message)
  }
  return safeString(value)
}

function getReasonKeys(value: unknown): string[] {
  if ((typeof value !== 'object' && typeof value !== 'function') || value === null) return []
  try {
    return Object.keys(value).slice(0, MAX_REASON_KEYS)
  } catch {
    return []
  }
}

function getReasonType(value: unknown): string {
  if (value === null) return 'null'
  if (Array.isArray(value)) return 'array'
  const valueType = typeof value
  if (valueType === 'function') return 'function'
  return valueType
}

function getErrorType(value: unknown): string {
  const name = safeString(getProperty(value, 'name')).trim()
  return name || getReasonType(value)
}

function getSafePageUrl(value: unknown): string | undefined {
  if (typeof value !== 'string' || value.trim() === '') return undefined
  try {
    const url = new URL(value)
    url.username = ''
    url.password = ''
    url.search = ''
    url.hash = ''
    return url.toString()
  } catch {
    return undefined
  }
}

function getNonErrorRejectionSummary(value: unknown): string {
  const reasonType = getReasonType(value)
  const keys = getReasonKeys(value)
  return keys.length > 0
    ? `Non-Error rejection (${reasonType}; keys: ${keys.join(',')})`
    : `Non-Error rejection (${reasonType})`
}

function isTokenExpiredMessage(message: unknown): boolean {
  const text = getMessageText(message)
  if (TOKEN_EXPIRED_PATTERNS.some((pattern) => text.includes(pattern))) return true
  const lower = text.toLowerCase()
  return lower.includes('jwt expired') || lower.includes('token expired')
}

function hasTokenExpiredShape(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false
  return (
    getProperty(value, 'name') === 'TokenExpiredError' ||
    isTokenExpiredMessage(getProperty(value, 'message'))
  )
}

function getErrorLikeName(value: unknown): string {
  return safeString(getProperty(value, 'name'))
}

function getErrorLikeMessage(value: unknown): string {
  return getMessageText(value)
}

function isKnownValidationNoise(message: unknown): boolean {
  const text = getMessageText(message)
  if (SENTRY_VALIDATION_NOISE_PATTERNS.some((pattern) => text.includes(pattern))) return true
  const lower = text.toLowerCase()
  return (
    lower.includes('codex') &&
    (lower.includes('sentry') ||
      lower.includes('dingtalk') ||
      lower.includes('smoke') ||
      lower.includes('validation') ||
      lower.includes('verification'))
  )
}

function isOcrDownloadNoise(message: unknown): boolean {
  const text = getMessageText(message)
  return OCR_DOWNLOAD_NOISE_PATTERNS.some((pattern) => text.includes(pattern))
}

function isIgnorableNetworkError(name: string, message: unknown): boolean {
  const text = getMessageText(message)
  if (text.includes('api_proxy:')) return false
  return name === 'NetworkError' || text.startsWith('NetworkError:')
}

function isLocalhostUrl(value: unknown): boolean {
  if (typeof value !== 'string' || value.trim() === '') return false
  try {
    const url = new URL(value)
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return false
    return url.hostname === 'localhost' || url.hostname === '127.0.0.1' || url.hostname === '[::1]'
  } catch {
    return false
  }
}

export function isLocalPreviewUrl(value: unknown): boolean {
  if (typeof value !== 'string' || value.trim() === '') return false
  try {
    const url = new URL(value)
    if (url.protocol === 'tauri:') {
      return url.hostname === 'localhost' && import.meta.env.DEV
    }
    return isLocalhostUrl(value)
  } catch {
    return false
  }
}

export function resolveSentryEnvironment(
  configuredEnvironment: string | undefined,
  currentUrl: string | undefined,
): string {
  const environment = configuredEnvironment || 'development'
  if (environment === 'production' && isLocalPreviewUrl(currentUrl)) {
    return 'development'
  }
  return environment
}

function isProductionLocalhostEvent(
  event: Pick<Sentry.Event, 'request' | 'tags'>,
  environment: string,
): boolean {
  if (environment !== 'production') return false
  return isLocalPreviewUrl(event.request?.url) || isLocalPreviewUrl(event.tags?.url)
}

export function shouldDropSentryEvent(
  event: Pick<Sentry.Event, 'exception' | 'message' | 'request' | 'tags'>,
  hint?: Pick<Sentry.EventHint, 'originalException'>,
  environment = 'development',
): boolean {
  if (isProductionLocalhostEvent(event, environment)) return true
  if (hasTokenExpiredShape(hint?.originalException)) return true
  if (
    isIgnorableNetworkError(
      getErrorLikeName(hint?.originalException),
      getErrorLikeMessage(hint?.originalException),
    )
  ) {
    return true
  }

  if (event.message && isTokenExpiredMessage(event.message)) return true
  if (event.message && isKnownValidationNoise(event.message)) return true
  if (event.message && isOcrDownloadNoise(event.message)) return true
  if (event.message && isIgnorableNetworkError('', event.message)) return true

  const exceptions = event.exception?.values ?? []
  return exceptions.some((exception) => {
    const value = exception.value ?? ''
    return (
      exception.type === 'TokenExpiredError' ||
      isTokenExpiredMessage(value) ||
      isKnownValidationNoise(value) ||
      isOcrDownloadNoise(value) ||
      isIgnorableNetworkError(exception.type ?? '', value)
    )
  })
}

function isPlainNonErrorRejection(value: unknown): boolean {
  return Boolean(
    value &&
    !(value instanceof Error) &&
    (typeof value === 'object' || typeof value === 'function'),
  )
}

function redactNonErrorRejectionEvent(
  event: Sentry.Event,
  hint?: Pick<Sentry.EventHint, 'originalException'>,
): void {
  const originalException = hint?.originalException
  if (!isPlainNonErrorRejection(originalException)) return

  const summary = getNonErrorRejectionSummary(originalException)
  event.message = summary
  event.exception = {
    ...event.exception,
    values: event.exception?.values?.map((exception) => ({
      ...exception,
      type: exception.type || 'NonErrorRejection',
      value: summary,
    })) || [{ type: 'NonErrorRejection', value: summary }],
  }
  event.extra = {
    ...event.extra,
    reasonType: getReasonType(originalException),
    reasonKeys: getReasonKeys(originalException),
  }
}

export function initSentry(app: App, entry: SentryEntry): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  const buildVersion = import.meta.env.VITE_APP_VERSION?.trim() || __APP_VERSION__
  const release = getSentryRelease(buildVersion)
  const environment = resolveSentryEnvironment(
    import.meta.env.VITE_APP_ENV || import.meta.env.MODE,
    globalThis.location?.href,
  )
  const statusData = { entry, environment, release, buildVersion }

  if (!dsn?.trim()) {
    logger.info('Sentry', 'sentry_disabled', {
      ...statusData,
      status: 'disabled',
      reasonCode: 'dsn_missing',
    })
    return
  }
  if (!shouldInitSentry(dsn)) {
    logger.warn('Sentry', 'sentry_disabled', {
      ...statusData,
      status: 'disabled',
      reasonCode: 'dsn_invalid',
    })
    return
  }

  const isDev = import.meta.env.DEV
  const integrations =
    entry === 'main'
      ? [Sentry.browserTracingIntegration()]
      : [Sentry.globalHandlersIntegration({ onerror: true, onunhandledrejection: true })]

  try {
    Sentry.init({
      app,
      dsn,
      release,
      environment,
      integrations,
      autoSessionTracking: false,
      sampleRate: 1.0,
      tracesSampleRate: getSentryTracesSampleRate(environment),
      allowUrls: getSentryAllowUrls(environment),
      ignoreErrors: getSentryIgnoreErrors(),
      beforeSend(event, hint) {
        if (isDev) {
          console.error('[Sentry Dev]', event, hint)
          return null
        }
        if (shouldDropSentryEvent(event, hint, environment)) return null
        redactNonErrorRejectionEvent(event, hint)

        if (event.request) {
          event.request.url = getSafePageUrl(event.request.url ?? globalThis.location?.href)
          delete event.request.cookies
          delete event.request.data
          delete event.request.headers
          delete event.request.query_string
        }
        event.tags = {
          ...event.tags,
          process: 'webview',
          entry,
          build_version: buildVersion,
        }
        for (const [key, value] of Object.entries(event.tags)) {
          if (value == null || (typeof value === 'string' && !value.trim())) {
            delete event.tags[key]
          }
        }
        return event
      },
    })

    logger.info('Sentry', 'sentry_initialized', {
      ...statusData,
      status: 'enabled',
    })

    if (environment === 'staging') {
      Sentry.addBreadcrumb({
        category: 'app.lifecycle',
        message: 'webview_sentry_initialized',
        level: 'info',
        data: { entry, buildVersion },
      })
    }
  } catch (error) {
    logger.error('Sentry', 'sentry_init_failed', {
      ...statusData,
      status: 'disabled',
      reasonCode: 'init_failed',
      errorType: getErrorType(error),
    })
  }
}

export function setSentryUserContext(user: UserInfo): void {
  Sentry.setUser({
    id: String(user.id),
    username: user.username,
  })
  Sentry.setTags({
    'user.id': String(user.id),
    'company.id': String(user.companyId || 0),
    'department.id': String(user.departmentId || 0),
    'role.id': String(user.roleId || 0),
    'role.name': user.roleName?.trim() || undefined,
    data_scope: user.dataScope?.trim() || undefined,
  })
}

export function clearSentryUserContext(): void {
  Sentry.setUser(null)
  Sentry.setTag('user.id', undefined)
  Sentry.setTag('company.id', undefined)
  Sentry.setTag('department.id', undefined)
  Sentry.setTag('role.id', undefined)
  Sentry.setTag('role.name', undefined)
  Sentry.setTag('data_scope', undefined)
}
